import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TableStage from "./TableStage.jsx";
import ReadStage from "./ReadStage.jsx";
import StandoffStage from "./StandoffStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./TheStandoff.css";

const STAGES = ["intro", "table", "read", "standoff", "debrief"];
const STAGE_LABELS = { table: "Read The Table", read: "Between The Lines", standoff: "The Standoff", debrief: "Deal Report" };

export default function TheStandoff() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [tableResult, setTableResult] = useState(null);
  const [readResult, setReadResult] = useState(null);
  const [standoffResult, setStandoffResult] = useState(null);

  const restart = () => {
    setTableResult(null);
    setReadResult(null);
    setStandoffResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-magenta">Level 11</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">The Standoff</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["table", "read", "standoff", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="ts-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Sit down across the table from someone who wants the exact opposite outcome you
                do, and somehow you both need to walk away thinking you won. Nerves show up first
                — in your voice, in a silence you can't stand to leave alone, in the number you
                blink out too fast. The <span className="text-cyan">room</span> can be read like
                anything else, once you know what you're looking at.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: read the table for the tactic already being run on you, sort out the
                psychology working underneath the numbers, then sit through a standoff where the
                only way through is holding your nerve on purpose.
              </p>
              <button onClick={() => setStage("table")} className="btn-magenta">
                ▶ TAKE A SEAT
              </button>
            </div>
          )}

          {stage === "table" && (
            <TableStage
              onComplete={(result) => {
                setTableResult(result);
                setStage("read");
              }}
            />
          )}

          {stage === "read" && (
            <ReadStage
              onComplete={(result) => {
                setReadResult(result);
                setStage("standoff");
              }}
            />
          )}

          {stage === "standoff" && tableResult && readResult && (
            <StandoffStage
              onComplete={(result) => {
                setStandoffResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && tableResult && readResult && standoffResult && (
            <DebriefStage
              tableResult={tableResult}
              readResult={readResult}
              standoffResult={standoffResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
